import { createFileRoute } from '@tanstack/react-router';
import { Clock, Plus, User as UserIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppointmentCard } from '@/components/appointments/appointment-card';
import { AppointmentForm } from '@/components/appointments/appointment-form';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useAppointments,
  useClaimAppointment,
  useCreateAppointment,
  useDeleteAppointment,
  useUnclaimAppointment,
  useUpdateAppointment,
} from '@/hooks/use-appointments';
import { useUsers } from '@/hooks/use-users';
import { useAuth } from '@/providers/auth-provider';
import type { User } from '@/services/api';
import type { Appointment, CreateAppointmentRequest } from '@/types/appointment';

export const Route = createFileRoute('/appointments')({
  component: () => (
    <ProtectedRoute>
      <AppointmentsPage />
    </ProtectedRoute>
  ),
});

function AppointmentsPage() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [unclaimingAppointmentId, setUnclaimingAppointmentId] = useState<string | null>(null);
  const [claimingAppointmentId, setClaimingAppointmentId] = useState<string | null>(null);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [deletingAppointment, setDeletingAppointment] = useState<Appointment | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const { data: appointmentsData, isLoading, error } = useAppointments();
  const createAppointmentMutation = useCreateAppointment();
  const claimAppointmentMutation = useClaimAppointment();
  const unclaimAppointmentMutation = useUnclaimAppointment();
  const updateAppointmentMutation = useUpdateAppointment();
  const deleteAppointmentMutation = useDeleteAppointment();
  const { data: usersData } = useUsers();

  const users: Record<string, User> = usersData?.users || {};
  const allAppointments = appointmentsData?.appointments || [];
  const now = new Date();

  const myUpcomingAppointments = allAppointments
    .filter((appointment) => appointment.assignedTo === session?.user?.id && new Date(appointment.startTime) >= now)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  const othersUpcomingAppointments = allAppointments
    .filter(
      (appointment) =>
        ((appointment.assignedTo !== session?.user?.id && appointment.assignedTo) || !appointment.assignedTo) &&
        new Date(appointment.startTime) >= now,
    )
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  const pastAppointments = allAppointments
    .filter((appointment) => new Date(appointment.startTime) < now)
    .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

  const handleCreateAppointment = async (data: CreateAppointmentRequest) => {
    try {
      await createAppointmentMutation.mutateAsync(data);
      setCreateDialogOpen(false);
    } catch (error) {
      console.error('Failed to create appointment:', error);
    }
  };

  const handleUnclaimAppointment = async (appointmentId: string) => {
    try {
      setUnclaimingAppointmentId(appointmentId);
      await unclaimAppointmentMutation.mutateAsync(appointmentId);
    } catch (error) {
      console.error('Failed to unclaim appointment:', error);
    } finally {
      setUnclaimingAppointmentId(null);
    }
  };

  const handleClaimAppointment = async (appointmentId: string) => {
    try {
      setClaimingAppointmentId(appointmentId);
      await claimAppointmentMutation.mutateAsync(appointmentId);
    } catch (error) {
      console.error('Failed to claim appointment:', error);
    } finally {
      setClaimingAppointmentId(null);
    }
  };

  const handleEditAppointment = (appointment: Appointment) => {
    setEditingAppointment(appointment);
  };

  const handleUpdateAppointment = async (data: CreateAppointmentRequest) => {
    if (!editingAppointment) return;

    try {
      await updateAppointmentMutation.mutateAsync({
        id: editingAppointment.id,
        data,
      });
      setEditingAppointment(null);
    } catch (error) {
      console.error('Failed to update appointment:', error);
    }
  };

  const handleDeleteClick = (appointment: Appointment) => {
    setDeletingAppointment(appointment);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingAppointment) return;

    try {
      await deleteAppointmentMutation.mutateAsync(deletingAppointment.id);
      setShowDeleteDialog(false);
      setDeletingAppointment(null);
    } catch (error) {
      console.error('Failed to delete appointment:', error);
    }
  };

  const renderAppointmentCard = (appointment: Appointment, isMyAppointment = false) => {
    let actionButton: ReactNode;

    if (isMyAppointment) {
      actionButton = (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleUnclaimAppointment(appointment.id)}
          disabled={unclaimingAppointmentId === appointment.id}
          className="border-red-300 text-red-600 hover:bg-red-50"
        >
          {unclaimingAppointmentId === appointment.id ? t('appointments.unclaiming') : t('appointments.unassign')}
        </Button>
      );
    } else if (!appointment.assignedTo) {
      actionButton = (
        <Button
          size="sm"
          onClick={() => handleClaimAppointment(appointment.id)}
          disabled={claimingAppointmentId === appointment.id}
          className="bg-green-600 hover:bg-green-700"
        >
          {claimingAppointmentId === appointment.id ? t('appointments.claiming') : t('appointments.claim')}
        </Button>
      );
    }

    return (
      <AppointmentCard
        key={appointment.id}
        appointment={appointment}
        variant={isMyAppointment ? 'assigned' : 'default'}
        users={users}
        currentUserId={session?.user?.id}
        onEdit={handleEditAppointment}
        onDelete={handleDeleteClick}
        actions={actionButton}
      />
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">{t('nav.appointments')}</h1>
        </div>
        <div className="text-center py-8">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">{t('nav.appointments')}</h1>
        </div>
        <div className="text-center py-8 text-red-600">{t('appointments.errorLoading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">{t('nav.appointments')}</h1>
        <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          {t('appointments.create')}
        </Button>
      </div>

      <Tabs defaultValue="my-appointments" className="w-full">
        <TabsList className="w-full">
          <TabsTrigger value="my-appointments" className="flex items-center gap-2 text-sm">
            {t('appointments.myAppointments')}
            {myUpcomingAppointments.length > 0 && (
              <Badge variant="default" className="text-xs bg-green-500">
                {myUpcomingAppointments.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="others-appointments" className="flex items-center gap-2 text-sm">
            {t('appointments.othersAppointments')}
            {othersUpcomingAppointments.length > 0 && (
              <Badge variant="default" className="text-xs bg-blue-500">
                {othersUpcomingAppointments.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="past-appointments" className="flex items-center gap-2 text-sm">
            {t('appointments.pastAppointments')}
            {pastAppointments.length > 0 && (
              <Badge variant="default" className="text-xs bg-gray-500">
                {pastAppointments.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="my-appointments" className="space-y-4">
          {myUpcomingAppointments.length === 0 ? (
            <EmptyState
              icon={UserIcon}
              title={t('appointments.noUpcomingAppointments')}
              description={t('appointments.noAssignedAppointmentsDescription')}
            />
          ) : (
            <div className="grid gap-4 md:gap-6">
              {myUpcomingAppointments.map((appointment) => renderAppointmentCard(appointment, true))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="others-appointments" className="space-y-4">
          {othersUpcomingAppointments.length === 0 ? (
            <EmptyState
              icon={UserIcon}
              title={t('appointments.noOthersAppointments')}
              description={t('appointments.noOthersAppointmentsDescription')}
            />
          ) : (
            <div className="grid gap-4 md:gap-6">
              {othersUpcomingAppointments.map((appointment) => renderAppointmentCard(appointment, false))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="past-appointments" className="space-y-4">
          {pastAppointments.length === 0 ? (
            <EmptyState
              icon={Clock}
              title={t('appointments.noPastAppointments')}
              description={t('appointments.noPastAppointmentsDescription')}
            />
          ) : (
            <div className="grid gap-4 md:gap-6">
              {pastAppointments.map((appointment) => renderAppointmentCard(appointment, false))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AppointmentForm
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleCreateAppointment}
        isLoading={createAppointmentMutation.isPending}
        mode="create"
      />

      {editingAppointment && (
        <AppointmentForm
          open={true}
          onOpenChange={(open) => !open && setEditingAppointment(null)}
          onSubmit={handleUpdateAppointment}
          appointment={editingAppointment}
          isLoading={updateAppointmentMutation.isPending}
          mode="edit"
        />
      )}

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('appointments.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('appointments.deleteConfirmation')}
              {deletingAppointment && <div className="mt-2 font-medium">"{deletingAppointment.title}"</div>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              {t('forms.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleteAppointmentMutation.isPending}>
              {deleteAppointmentMutation.isPending ? t('appointments.deleting') : t('appointments.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
